import { c as _c } from "react/compiler-runtime";
import AddActionModal from 'modules/automation-rules/actions/components/add-action-modal';
import EditActions from 'modules/automation-rules/actions/components/edit-actions';
import { getDefaultActionValue } from 'modules/automation-rules/actions/utils/get-default-action-value';
import AddTriggerModal from 'modules/automation-rules/triggers/components/add-trigger-modal';
import EditTriggers, { EditTriggersProps } from 'modules/automation-rules/triggers/components/edit-triggers';
import { AutomationRuleTriggersEnum } from 'modules/automation-rules/triggers/enums/trigger-enum';
import { AutomationRulesTriggersEditType } from 'modules/automation-rules/triggers/types/automation-rule-triggers-edit-interface';
import { getDefaultTriggerValue } from 'modules/automation-rules/triggers/utils/get-default-trigger-value';
import { AutomationRuleErrorInterface, AutomationRuleViewInterface } from 'modules/automation-rules/types/automation-rule-interface';
import React, { Dispatch, SetStateAction } from 'react';
import { Loader } from 'shared/components/loader';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { AutomationRuleActionIcon, AutomationRuleTriggerIcon } from '@systemeio/ui/icons';
interface RuleProps extends Pick<EditTriggersProps, 'hideTriggers'> {
  tempState: AutomationRuleViewInterface;
  setTempState: Dispatch<SetStateAction<AutomationRuleViewInterface>>;
  errors: AutomationRuleErrorInterface;
  maxTriggerId: React.MutableRefObject<number>;
  maxActionId: React.MutableRefObject<number>;
  isPreFetching?: boolean;
  isFetching: boolean;
  customAddTriggerHandler?: (trigger: AutomationRuleTriggersEnum, maxTriggerId: React.MutableRefObject<number>) => AutomationRulesTriggersEditType;
  customTriggerTitle?: React.ReactNode;
  allowedTriggers?: AutomationRuleTriggersEnum[];
  isStaticTriggers?: boolean;
}
export function Rule(t0) {
  const $ = _c(71);
  const {
    isPreFetching,
    isFetching,
    maxActionId,
    maxTriggerId,
    tempState,
    setTempState,
    errors,
    customAddTriggerHandler,
    customTriggerTitle,
    allowedTriggers,
    isStaticTriggers,
    hideTriggers
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const canAdd = !isFetching && !isPreFetching;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AutomationRuleTriggerIcon />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== customTriggerTitle || $[2] !== t) {
    t2 = customTriggerTitle ?? <span className="text-text-primary text-[17px] font-bold">{t("dashboard.automation_rule.trigger.title")}</span>;
    $[1] = customTriggerTitle;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("dashboard.automation_rule.edit.when_happens");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <span className="text-text-primary">{t3}</span>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <div className="flex items-center gap-3">{t1}<div className="flex flex-col gap-1">{t2}{t4}</div></div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== customAddTriggerHandler || $[12] !== maxTriggerId || $[13] !== setTempState) {
    t6 = trigger => setTempState(prev => ({
      ...prev,
      triggers: [...prev.triggers, customAddTriggerHandler ? customAddTriggerHandler(trigger, maxTriggerId) : getDefaultTriggerValue(trigger, maxTriggerId)]
    }));
    $[11] = customAddTriggerHandler;
    $[12] = maxTriggerId;
    $[13] = setTempState;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== allowedTriggers || $[16] !== canAdd || $[17] !== t6) {
    t7 = <AddTriggerModal allowedTriggers={allowedTriggers} isReady={canAdd} onAddTrigger={t6} />;
    $[15] = allowedTriggers;
    $[16] = canAdd;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t5 || $[20] !== t7) {
    t8 = <div className="bg-background-primary/50 px-20 py-5"><div className="flex justify-between gap-5">{t5}{t7}</div></div>;
    $[19] = t5;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = `relative flex min-h-[500px] flex-col gap-16 px-20 py-5 ${isPreFetching && "justify-center"}`;
  let t10;
  if ($[22] !== errors || $[23] !== hideTriggers || $[24] !== isPreFetching || $[25] !== isStaticTriggers || $[26] !== setTempState || $[27] !== tempState) {
    t10 = isPreFetching ? <div className="flex h-full items-center justify-center"><Loader className="scale-150" /></div> : <EditTriggers isStatic={isStaticTriggers} hideTriggers={hideTriggers} errors={errors} setRules={setTempState} triggers={tempState.triggers} />;
    $[22] = errors;
    $[23] = hideTriggers;
    $[24] = isPreFetching;
    $[25] = isStaticTriggers;
    $[26] = setTempState;
    $[27] = tempState;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="bg-background-secondary border-text-secondary/30 absolute inset-x-full inset-y-1/2 hidden h-[30px] w-[30px] -translate-x-1/2 -translate-y-1/2 rotate-45 border-r border-t lg:inline" />;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t10 || $[31] !== t9) {
    t12 = <div className={t9}>{t10}{t11}</div>;
    $[30] = t10;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t12 || $[34] !== t8) {
    t13 = <div className="border-text-secondary/30 bg-background-secondary flex w-full flex-col rounded-lg lg:w-1/2 lg:rounded-none lg:border-r">{t8}{t12}</div>;
    $[33] = t12;
    $[34] = t8;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <AutomationRuleActionIcon />;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t) {
    t15 = t("dashboard.automation_rule.action.title");
    $[37] = t;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t15) {
    t16 = <span className="text-text-primary text-[17px] font-bold">{t15}</span>;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t) {
    t17 = t("dashboard.automation_rule.edit.then_happens");
    $[41] = t;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t17) {
    t18 = <span className="text-text-primary">{t17}</span>;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t16 || $[46] !== t18) {
    t19 = <div className="flex items-center gap-3">{t14}<div className="flex flex-col gap-1">{t16}{t18}</div></div>;
    $[45] = t16;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== maxActionId || $[49] !== setTempState) {
    t20 = action => setTempState(prev_0 => ({
      ...prev_0,
      actions: [...prev_0.actions, {
        ...getDefaultActionValue(action, maxActionId)
      }]
    }));
    $[48] = maxActionId;
    $[49] = setTempState;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== canAdd || $[52] !== t20) {
    t21 = <AddActionModal isReady={canAdd} onAddAction={t20} />;
    $[51] = canAdd;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t19 || $[55] !== t21) {
    t22 = <div className="bg-background-primary/50 px-20 py-5"><div className="flex justify-between gap-5">{t19}{t21}</div></div>;
    $[54] = t19;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  const t23 = `relative flex min-h-[500px] flex-col gap-16 px-20 py-5 ${isPreFetching && "justify-center"}`;
  let t24;
  if ($[57] !== errors || $[58] !== isPreFetching || $[59] !== setTempState || $[60] !== tempState) {
    t24 = isPreFetching ? <div className="flex h-full items-center justify-center"><Loader className="scale-150" /></div> : <EditActions errors={errors} setRules={setTempState} actions={tempState.actions} />;
    $[57] = errors;
    $[58] = isPreFetching;
    $[59] = setTempState;
    $[60] = tempState;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t23 || $[63] !== t24) {
    t25 = <div className={t23}>{t24}</div>;
    $[62] = t23;
    $[63] = t24;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== t22 || $[66] !== t25) {
    t26 = <div className="bg-background-secondary flex w-full flex-col rounded-lg lg:w-1/2 lg:rounded-none">{t22}{t25}</div>;
    $[65] = t22;
    $[66] = t25;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t13 || $[69] !== t26) {
    t27 = <div className="overflow-x-hidden lg:rounded-lg"><div className="flex flex-col gap-5 lg:flex-row lg:gap-0">{t13}{t26}</div></div>;
    $[68] = t13;
    $[69] = t26;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  return t27;
}